import React, { useState } from "react";

/**
 * SAT-HTC Mastermatrix — native SVG mandala visualization.
 * 12 codes around, 5 movements as inner ring, "Love Is Transformation" at heart.
 * Interactive: hover on a code highlights it and shows its leitfrage in a side panel.
 */
const CODES = [
  { key: "wesen",       label: "Wesen",       q: "Wer bin ich ursprünglich?" },
  { key: "herkunft",    label: "Herkunft",    q: "Was trage ich aus meinem Familiensystem?" },
  { key: "name",        label: "Name",        q: "Welche Identität wirkt durch meinen Namen?" },
  { key: "schutz",      label: "Schutz",      q: "Wie habe ich gelernt zu überleben?" },
  { key: "schatten",    label: "Schatten",    q: "Was wurde verdrängt und wirkt im Untergrund?" },
  { key: "herz",        label: "Herz",        q: "Wie liebe ich und wo wurde Liebe verletzt?" },
  { key: "glauben",     label: "Glauben",     q: "Welche inneren Sätze erschaffen meine Realität?" },
  { key: "körper",      label: "Körper",      q: "Was sagt mein Körper, was mein Kopf nicht hören will?" },
  { key: "beziehung",   label: "Beziehung",   q: "Welchen alten Schmerz spiegelt mir das Du?" },
  { key: "berufung",    label: "Berufung",    q: "Welche Gabe will durch mich wirken?" },
  { key: "bewusstsein", label: "Bewusstsein", q: "Wieviel Wahrheit und Verantwortung kann ich halten?" },
  { key: "vermächtnis", label: "Vermächtnis", q: "Welche Frucht soll mein Leben hinterlassen?" },
];

const MOVEMENTS = ["Decode", "Feel", "Recode", "Embody", "Serve"];

export default function HtcMasterMatrix({ size = 720 }) {
  const [active, setActive] = useState(null);
  const cx = size / 2;
  const cy = size / 2;

  const rOuterLabel = size * 0.46;   // where the 12 code labels sit
  const rOuterRing  = size * 0.395;  // outer decorative ring
  const rInnerRing  = size * 0.30;   // movement ring outer
  const rMovementLbl = size * 0.255; // movement labels
  const rCore       = size * 0.145;  // core circle (love is transformation)

  const codePos = (i) => {
    // Start at top (–90°), go clockwise
    const angle = (-90 + (360 / 12) * i) * (Math.PI / 180);
    return {
      x: cx + rOuterLabel * Math.cos(angle),
      y: cy + rOuterLabel * Math.sin(angle),
      xInner: cx + (rOuterRing - 8) * Math.cos(angle),
      yInner: cy + (rOuterRing - 8) * Math.sin(angle),
      angleDeg: (-90 + (360 / 12) * i),
    };
  };

  return (
    <div className="w-full flex flex-col lg:flex-row items-center gap-8" data-testid="htc-master-matrix">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full max-w-[720px] h-auto select-none"
        role="img"
        aria-label="SAT-HTC Human Transformation Code Mastermatrix der SAT Akademie mit 12 Codes, 5 Transformationsbewegungen und Love Is Transformation im Zentrum."
      >
        <defs>
          <radialGradient id="htc-bg" cx="50%" cy="50%" r="60%">
            <stop offset="0%"   stopColor="#1a1140" stopOpacity="0.9" />
            <stop offset="55%"  stopColor="#0a0f28" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#050814" stopOpacity="1" />
          </radialGradient>
          <radialGradient id="htc-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#D4AF37" stopOpacity="0.85" />
            <stop offset="70%"  stopColor="#7C4FBA" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#7C4FBA" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="htc-arrow" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#7C4FBA" />
            <stop offset="100%" stopColor="#D4AF37" />
          </linearGradient>
        </defs>

        {/* Cosmic background */}
        <circle cx={cx} cy={cy} r={size * 0.49} fill="url(#htc-bg)" />
        {/* Star particles */}
        {[...Array(60)].map((_, i) => {
          const a = (i * 37) % 360;
          const rr = (size * 0.28) + ((i * 13) % (size * 0.18));
          const x = cx + rr * Math.cos((a * Math.PI) / 180);
          const y = cy + rr * Math.sin((a * Math.PI) / 180);
          return <circle key={i} cx={x} cy={y} r={(i % 3) * 0.7 + 0.6} fill="#D4AF37" opacity={0.15 + (i % 5) * 0.08} />;
        })}

        {/* Outer decorative ring */}
        <circle cx={cx} cy={cy} r={rOuterRing} fill="none" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="1" />
        <circle cx={cx} cy={cy} r={rOuterRing + 12} fill="none" stroke="#D4AF37" strokeOpacity="0.15" strokeWidth="0.5" />

        {/* Sacred-geometry lattice — 12 diagonals */}
        {CODES.map((_, i) => {
          const angle = (-90 + (360 / 12) * i) * (Math.PI / 180);
          return (
            <line
              key={i}
              x1={cx + rInnerRing * Math.cos(angle)}
              y1={cy + rInnerRing * Math.sin(angle)}
              x2={cx + rOuterRing * Math.cos(angle)}
              y2={cy + rOuterRing * Math.sin(angle)}
              stroke="#7C4FBA"
              strokeOpacity="0.35"
              strokeWidth="0.8"
            />
          );
        })}

        {/* Inner movement ring */}
        <circle cx={cx} cy={cy} r={rInnerRing} fill="none" stroke="#7C4FBA" strokeOpacity="0.55" strokeWidth="1" />
        <circle cx={cx} cy={cy} r={rInnerRing - 22} fill="none" stroke="#7C4FBA" strokeOpacity="0.25" strokeWidth="0.6" />

        {/* Rotating dashed ring for motion feel (static) */}
        <circle cx={cx} cy={cy} r={rInnerRing + 6} fill="none" stroke="#D4AF37" strokeOpacity="0.25"
                strokeWidth="0.8" strokeDasharray="2 6" />

        {/* Movement labels (5) — evenly spaced, starting at top */}
        {MOVEMENTS.map((m, i) => {
          const angle = (-90 + (360 / 5) * i) * (Math.PI / 180);
          const x = cx + rMovementLbl * Math.cos(angle);
          const y = cy + rMovementLbl * Math.sin(angle);
          return (
            <g key={m}>
              <circle cx={x} cy={y} r="4" fill="#D4AF37" />
              <text
                x={x}
                y={y - 12}
                textAnchor="middle"
                fill="#D4AF37"
                fontFamily="'Playfair Display', serif"
                fontSize={size * 0.022}
                letterSpacing="0.18em"
                style={{ textTransform: "uppercase" }}
              >
                {m}
              </text>
            </g>
          );
        })}

        {/* Core circle with heart + "Love Is Transformation" */}
        <circle cx={cx} cy={cy} r={rCore + 6} fill="url(#htc-core)" />
        <circle cx={cx} cy={cy} r={rCore} fill="#081328" stroke="#D4AF37" strokeOpacity="0.7" strokeWidth="1.2" />
        {/* Heart */}
        <g transform={`translate(${cx}, ${cy - 6})`}>
          <path
            d="M0,10 C-14,-2 -18,-18 -8,-22 C-2,-24 0,-18 0,-14 C0,-18 2,-24 8,-22 C18,-18 14,-2 0,10 Z"
            fill="#D4AF37"
            opacity="0.95"
          />
        </g>
        <text
          x={cx}
          y={cy + 24}
          textAnchor="middle"
          fill="#F5EBE0"
          fontFamily="'Playfair Display', serif"
          fontSize={size * 0.018}
          letterSpacing="0.24em"
          style={{ textTransform: "uppercase" }}
        >
          Love Is
        </text>
        <text
          x={cx}
          y={cy + 24 + size * 0.028}
          textAnchor="middle"
          fill="#D4AF37"
          fontFamily="'Playfair Display', serif"
          fontSize={size * 0.024}
          letterSpacing="0.24em"
          style={{ textTransform: "uppercase" }}
        >
          Transformation
        </text>

        {/* 12 Code labels around the outer ring */}
        {CODES.map((c, i) => {
          const p = codePos(i);
          const isActive = active === c.key;
          return (
            <g
              key={c.key}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setActive(c.key)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(c.key)}
              onBlur={() => setActive(null)}
              tabIndex="0"
              role="button"
              aria-label={`${c.label}: ${c.q}`}
              data-testid={`htc-code-node-${c.key}`}
            >
              {/* Node dot */}
              <circle
                cx={p.xInner}
                cy={p.yInner}
                r={isActive ? 6 : 4}
                fill={isActive ? "#D4AF37" : "#F5EBE0"}
                opacity={isActive ? 1 : 0.85}
              />
              {/* Label */}
              <text
                x={p.x}
                y={p.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={isActive ? "#D4AF37" : "#F5EBE0"}
                fontFamily="'Playfair Display', serif"
                fontSize={size * 0.024}
                letterSpacing="0.14em"
                style={{ textTransform: "uppercase", transition: "fill 0.3s" }}
              >
                {c.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Info panel — shown when a code is hovered */}
      <div
        className="w-full lg:w-72 min-h-[180px] flex flex-col justify-center bg-white/[0.03] border border-[#D4AF37]/20 rounded-2xl p-6"
        aria-live="polite"
        data-testid="htc-matrix-panel"
      >
        {active ? (
          <>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]/80 mb-2">
              {CODES.find((c) => c.key === active).label}-Code
            </div>
            <div className="serif italic text-xl md:text-2xl text-white leading-snug">
              „{CODES.find((c) => c.key === active).q}"
            </div>
          </>
        ) : (
          <>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-2">
              12 Codes · 5 Bewegungen
            </div>
            <div className="serif italic text-lg text-white/70 leading-snug">
              Fahre mit der Maus über einen Code, um seine Leitfrage zu sehen.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
